import { useRef } from 'react';
import { useAtomValue } from 'jotai';
import { cn } from '@rocco/utils/cn';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ProductTileViewModel } from '@rocco/ui/tile-product/view-models/product';
import { TileMedia } from '@rocco/ui/tile-shared/components/TileMedia';
import { TileBadge } from '@rocco/ui/tile-shared/components/TileBadge';

import { ProductTileVariants } from './ProductTileVariants';
import { ProductTilePrice } from './ProductTilePrice';
import { TileFollowButton } from '@rocco/ui/tile-shared/components/TileFollowButton';
import { uiContextAtomTileActions } from '@rocco/ui/tile-shared/hooks/use-shared-tiles';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import { uiContextAtomViewTransitionsEnabled } from '@rocco/states/uiContextAtomViewTransitions';
import { useTileViewTransitionScope } from '@rocco/contexts/TileViewTransitionScopeContext';
import { isSalesModeAtom } from '@rocco/states/apAdminUiContext';
import { useTracker } from '../../../../../tracker';
import { trackTileMediaDetailClick } from '../../../../../web/app/modules/tracking/util/trackTileMedia';
import { OptionalLink } from '@rocco/components/link/OptionalLink';
import { TILE_COMPONENTS_CLASS_NAMES } from '@rocco/ui/tile-shared/utils/contstants';
import {
    setPendingHeroImage,
    usePrefersReducedMotion,
    useTileHeroViewTransitionStyle,
} from '@rocco/ui/tile-shared/hooks/use-tile-hero-view-transition';

export const ProductTile = (props: TileViewModel<ProductTileViewModel>) => {
    const {
        aspectRatio,
        model: product,
        section,
        disableDesignBoard,
        enableTileSlider,
        isFollowed,
        onFollow,
        hideFollowButton = false,
        followButton,
        overlay,
        inlineActionButtons,
        disableLink = false,
        onClick,
        className,
        contentEditable,
        eager,
        rounded = true,
        imageSizes,
        compactBrowseText = false,
    } = props;
    const { isMobile } = useAtomValue(uiContextAtomDisplayMode);
    // 5.0 directory browse: the portrait tile shows the larger detail-page
    // typography (22px title + supplier) rather than the default 16px.
    const isPortrait = aspectRatio === 'portrait';
    const tileActions = useAtomValue(uiContextAtomTileActions);
    const tileRef = useRef<HTMLAnchorElement>(null);
    const tracker = useTracker();
    const isSalesMode = useAtomValue(isSalesModeAtom);
    // Flag AND surface scope: only tiles on listing surfaces (category /
    // browse pages wrap their content in TileViewTransitionScope) take part —
    // a tile on a detail page or the homepage navigates plainly. Both hooks
    // run unconditionally (no short-circuit) to keep the hook order stable.
    const viewTransitionsFlagOn = useAtomValue(
        uiContextAtomViewTransitionsEnabled,
    );
    const inTileTransitionScope = useTileViewTransitionScope();
    // Reduced motion opts out of the whole flow — no view transition (no
    // snapshot capture at all) and no app-mode chrome; the page behaves
    // exactly like a plain flag-off navigation.
    const prefersReducedMotion = usePrefersReducedMotion();
    const appModeEnabled =
        viewTransitionsFlagOn && inTileTransitionScope && !prefersReducedMotion;
    const viewTransitionsEnabled = appModeEnabled;
    const heroStyle = useTileHeroViewTransitionStyle(
        disableLink || !viewTransitionsEnabled ? undefined : product.link,
    );

    const draftOrPending = product.draft || product.pendingApproval;
    const statusLabel = draftOrPending
        ? product.draft
            ? 'Draft'
            : 'Pending Approval'
        : product.badge;

    const handleTileClick = () => {
        onClick?.();
        if (disableLink) {
            return;
        }

        if (viewTransitionsEnabled) {
            setPendingHeroImage(
                product.link,
                tileRef.current?.querySelector('img')?.currentSrc,
            );
        }

        tileActions?.trackingConfig?.onClick?.(
            tileRef,
            product.id,
            product.professional.id,
            section,
        );

        trackTileMediaDetailClick(tracker, {
            itemID: product.id,
            itemType: 'ProductTile',
            itemName: product.title,
        });
    };

    return (
        <OptionalLink
            ref={tileRef}
            mode={disableLink ? 'override' : 'normal'}
            to={product.link}
            prefetch="intent"
            viewTransition={viewTransitionsEnabled}
            state={appModeEnabled ? { detailTransition: true } : undefined}
            onClick={handleTileClick}
        >
            <div
                className={cn(
                    'flex h-full flex-col',
                    isPortrait ? 'gap-2.5' : 'gap-4',
                    className,
                )}
            >
                {/* The morphing box for the tile→detail view transition. The
                    hover/save overlays inside are part of the snapshot, which
                    is fine — the new page's crop dissolves over it early in
                    the flight. */}
                <div className="group relative h-auto" style={heroStyle}>
                    <TileMedia
                        aspectRatio={aspectRatio}
                        images={product.images}
                        video={product.video}
                        enableTileSlider={enableTileSlider}
                        rounded={rounded}
                        eager={eager}
                        sizes={imageSizes}
                        onSlideChange={(direction) => {
                            tileActions?.trackingConfig?.onTileMediaSliderSlideChange?.(
                                direction,
                                product.id,
                            );
                        }}
                    />

                    {overlay ? (
                        overlay
                    ) : (
                        <>
                            <div
                                className={cn(
                                    'absolute inset-0 hidden bg-gray-900/10 opacity-0 transition-opacity duration-300 md:block md:group-hover:opacity-100',
                                    rounded && 'rounded-tile',
                                )}
                            />
                            <div className="absolute top-3.5 right-4 flex gap-1 transition-opacity duration-300 md:invisible md:opacity-0 md:group-hover:visible md:group-hover:opacity-100">
                                {followButton
                                    ? followButton
                                    : !hideFollowButton &&
                                      !disableDesignBoard && (
                                          <TileFollowButton
                                              pinId={product.id}
                                              pinType="ProductPin"
                                              isFollowed={isFollowed}
                                              onFollow={onFollow}
                                          />
                                      )}
                            </div>
                        </>
                    )}

                    {/* Badge max-w keeps it clear of the 42px save control at
                        2-up mobile widths; Badge ellipsises natively. */}
                    {statusLabel && (
                        <div className="absolute top-4 left-4 flex max-md:max-w-[calc(100%-5rem)]">
                            <TileBadge title={statusLabel} />
                        </div>
                    )}
                </div>

                <div
                    className={cn(
                        'flex flex-col gap-4',
                        isPortrait && 'max-md:gap-0',
                    )}
                >
                    {inlineActionButtons && (
                        <div className="flex flex-col">
                            {inlineActionButtons}
                        </div>
                    )}
                    <div
                        className={cn(
                            TILE_COMPONENTS_CLASS_NAMES.TILE_DESCRIPTION_BLOCK,
                            'flex flex-col',
                        )}
                    >
                        <span
                            contentEditable={contentEditable?.enabled.title}
                            role={
                                contentEditable?.enabled.title
                                    ? 'textbox'
                                    : undefined
                            }
                            aria-label={
                                contentEditable?.enabled.title
                                    ? 'Edit title'
                                    : undefined
                            }
                            aria-multiline="false"
                            suppressContentEditableWarning={true}
                            onInput={(e: React.FormEvent<HTMLSpanElement>) =>
                                contentEditable?.onChange?.(
                                    (e.target as HTMLSpanElement).textContent ??
                                        '',
                                    'title',
                                )
                            }
                            onBlur={() => {
                                contentEditable?.onBlur?.();
                            }}
                            onKeyDown={(
                                e: React.KeyboardEvent<HTMLSpanElement>,
                            ) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    (e.target as HTMLSpanElement).blur();
                                }
                            }}
                            className={cn(
                                isPortrait
                                    ? cn(
                                          // Below md, match the compact
                                          // design-board tile title scale
                                          // (text-4.5/leading-none) instead of
                                          // the detail-page-sized 22px title.
                                          // md+ shows 22px, stepping down to
                                          // 16px when the grid asks for compact
                                          // browse text (5-6 column Display).
                                          'overflow-hidden font-medium text-ellipsis text-[#181818] max-md:text-[0.75rem] max-md:leading-[1.2]',
                                          compactBrowseText
                                              ? 'md:text-[1.375rem] md:leading-[1.3] lg:text-[1rem]'
                                              : 'md:text-[1.375rem] md:leading-[1.3]',
                                      )
                                    : 'overflow-hidden text-sm font-medium tracking-[0.03rem] text-ellipsis md:text-base md:tracking-[0.02rem]',
                                contentEditable?.enabled.title
                                    ? 'truncate'
                                    : isPortrait
                                      ? // On the narrow 2-up mobile browse grid,
                                        // let the title wrap to 2 lines instead of
                                        // truncating; reserve 2 lines' height so
                                        // rows stay aligned whether it runs 1 or 2
                                        // lines (2 × 1.125rem × 1 leading-none).
                                        // md+ keeps the single-line clamp.
                                        'max-md:line-clamp-2 max-md:min-h-[1.8rem] md:line-clamp-1'
                                      : 'line-clamp-1',
                                contentEditable?.enabled.title &&
                                    'focus-within:border-primary-500 -mx-2 cursor-text rounded-md border border-transparent px-2 focus-within:bg-gray-100 hover:border-gray-500',
                            )}
                        >
                            {product.title}
                        </span>
                        <span
                            contentEditable={
                                contentEditable?.enabled.description
                            }
                            role={
                                contentEditable?.enabled.description
                                    ? 'textbox'
                                    : undefined
                            }
                            aria-label={
                                contentEditable?.enabled.description
                                    ? 'Edit description'
                                    : undefined
                            }
                            aria-multiline="false"
                            suppressContentEditableWarning={true}
                            onInput={(e: React.FormEvent<HTMLSpanElement>) =>
                                contentEditable?.onChange?.(
                                    (e.target as HTMLSpanElement).textContent ??
                                        '',
                                    'description',
                                )
                            }
                            onBlur={() => {
                                contentEditable?.onBlur?.();
                            }}
                            onKeyDown={(
                                e: React.KeyboardEvent<HTMLSpanElement>,
                            ) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    (e.target as HTMLSpanElement).blur();
                                }
                            }}
                            className={cn(
                                isPortrait
                                    ? cn(
                                          // Quiet meta scale below md, matching
                                          // the design-board card's "Created by"
                                          // grey; md+ tracks the title size
                                          // (22px, or 16px in compact browse).
                                          'overflow-hidden font-normal text-ellipsis max-md:text-[0.75rem] max-md:leading-[1.3] max-md:text-gray-500 md:text-[#767676]',
                                          compactBrowseText
                                              ? 'md:text-[1.375rem] md:leading-[1.3] lg:text-[1rem]'
                                              : 'md:text-[1.375rem] md:leading-[1.3]',
                                      )
                                    : 'overflow-hidden text-sm font-medium tracking-[0.03rem] text-ellipsis text-gray-900/40 md:text-base md:tracking-[0.02rem]',
                                !contentEditable?.enabled.description &&
                                    'line-clamp-1',
                                contentEditable?.enabled.description &&
                                    'focus-within:border-primary-500 -ml-2 cursor-text rounded-md border border-transparent px-2 focus-within:bg-gray-100 hover:border-gray-500',
                            )}
                        >
                            {product.professional.name}
                        </span>
                    </div>

                    {product.price && !isSalesMode ? (
                        <div className="flex items-center justify-between gap-2 text-sm text-gray-900/40 md:text-base">
                            <ProductTilePrice
                                price={product.price}
                                compact={isPortrait}
                            />
                            {product.variants && (
                                <ProductTileVariants
                                    variants={product.variants}
                                    limit={isMobile ? 3 : 5}
                                    compact={isPortrait}
                                />
                            )}
                        </div>
                    ) : null}
                </div>
            </div>
        </OptionalLink>
    );
};
